import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Newspaper } from 'lucide-react';
import { toast } from 'sonner';
import mapi from './api';
import { MobileShell, MobileHeader, C } from './MobileShell';
import { formatDate } from '@/lib/utils';
import { useMobile } from './MobileContext';
import { isStaffArea } from './areas';

const EXCERPT = 140;
const IMAGE_URL = /https?:\/\/\S+\.(?:jpe?g|png|webp|gif)/gi;

export function newsExcerpt(body) {
  const text = String(body || '').replace(/\s+/g, ' ').trim();
  if (text.length <= EXCERPT) return text;
  const cut = text.slice(0, EXCERPT);
  const space = cut.lastIndexOf(' ');
  const base = space > 80 ? cut.slice(0, space) : cut;
  return `${base} …`;
}

function remember(list, item) {
  if (!item.url || list.some((entry) => entry.url === item.url)) return;
  list.push(item);
}

export function newsMedia(item) {
  const images = [];
  const files = [];
  const addImage = (url, name) => remember(images, { url, name: name || '' });
  const addFile = (url, name) => {
    if (!url || images.some((img) => img.url === url) || files.some((file) => file.url === url)) return;
    files.push({ url, name: name || 'Datei' });
  };
  if (item?.image_url) addImage(item.image_url, item.image_name);
  (item?.images || []).forEach((img) => {
    if (typeof img === 'string') addImage(img);
    else if (img?.url) addImage(img.url, img.name);
  });
  [...(item?.files || []), ...(item?.attachments || [])].forEach((file) => {
    const url = typeof file === 'string' ? file : file?.url;
    const name = typeof file === 'string' ? '' : (file?.name || file?.filename || '');
    const type = typeof file === 'string' ? '' : (file?.content_type || '');
    if (!url) return;
    if (type.startsWith('image/') || /\.(jpe?g|png|webp|gif)(\?|$)/i.test(url)) addImage(url, name);
    else addFile(url, name);
  });
  String(item?.body || '').match(IMAGE_URL)?.forEach((url) => {
    addImage(url.replace(/[),.;]+$/, ''));
  });
  return { images, files };
}

function Category() {
  return (
    <p className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: C.red }}>
      <Newspaper className="w-3.5 h-3.5" aria-hidden="true" />
      Aktuelles
    </p>
  );
}

export default function MobileNews() {
  const { area } = useMobile();
  const { newsId } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await mapi.get('/app/news');
      const all = r.data || [];
      setNews(isStaffArea(area) ? all : all.filter((n) => n.audience !== 'personal'));
    } catch (e) {
      toast.error('Aktuelles konnte nicht geladen werden');
    } finally {
      setLoaded(true);
    }
  }, [area]);
  useEffect(() => { load(); }, [load]);

  const fmt = (n) => formatDate(n.visible_from || n.created_at);
  const selected = newsId ? news.find((n) => String(n.id) === String(newsId)) : null;

  if (newsId && selected) {
    const { images, files } = newsMedia(selected);
    return (
      <MobileShell>
        <MobileHeader title="Aktuelles" onRefresh={load} backTo="/m/news" />
        <article className="flex-1 overflow-y-auto px-4 py-4 pb-10" data-testid="mobile-news-detail">
          <div
            className="rounded-2xl px-4 py-5"
            style={{
              background: C.card,
              border: `1px solid ${C.border}`,
              boxShadow: '0 1px 3px rgba(23, 32, 51, 0.06)',
            }}
          >
            <Category />
            <h2 className="text-xl font-bold mt-2 leading-snug" style={{ color: C.text }}>{selected.title}</h2>
            <p className="text-[11px] mt-2" style={{ color: C.dim }}>{fmt(selected)}</p>
            {images.map((img) => (
              <img
                key={img.url}
                src={img.url}
                alt=""
                data-testid="mobile-news-detail-image"
                className="w-full rounded-xl object-cover mt-4 max-h-64"
              />
            ))}
            {selected.body && (
              <p className="text-sm mt-4 whitespace-pre-wrap" style={{ color: C.text }}>{selected.body}</p>
            )}
            {files.map((file) => (
              <a
                key={file.url}
                href={file.url}
                data-testid="mobile-news-file"
                className="block text-sm font-semibold mt-3"
                style={{ color: C.red }}
              >
                {file.name}
              </a>
            ))}
          </div>
        </article>
      </MobileShell>
    );
  }

  return (
    <MobileShell>
      <MobileHeader title="Aktuelles" onRefresh={load} backTo="/m" />
      <div className="flex-1 overflow-y-auto px-4 py-4 pb-10">
        {loaded && !news.length && !newsId && (
          <p className="text-center text-sm mt-10" style={{ color: C.dim }}>Keine aktuellen Meldungen</p>
        )}
        {newsId && loaded && !selected && (
          <p className="text-center text-sm mt-10" style={{ color: C.dim }}>Beitrag nicht gefunden</p>
        )}
        {!newsId && news.map((n) => {
          const thumb = newsMedia(n).images[0];
          const excerpt = newsExcerpt(n.body);
          return (
            <button
              key={n.id}
              type="button"
              onClick={() => navigate(`/m/news/${n.id}`)}
              className="w-full text-left rounded-2xl mb-3 px-4 py-4"
              style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                boxShadow: '0 1px 3px rgba(23, 32, 51, 0.06)',
              }}
              data-testid={`mobile-news-${n.id}`}
            >
              <Category />
              <div className="flex gap-3 mt-2">
                <div className="min-w-0 flex-1">
                  <p className="text-lg font-bold leading-snug" style={{ color: C.text }}>{n.title}</p>
                  {excerpt && (
                    <p className="text-sm mt-1.5" style={{ color: C.text }}>{excerpt}</p>
                  )}
                </div>
                {thumb && (
                  <img
                    src={thumb.url}
                    alt=""
                    data-testid={`mobile-news-image-${n.id}`}
                    className="w-16 h-16 rounded-lg object-cover shrink-0"
                  />
                )}
              </div>
              <p className="text-[11px] mt-3" style={{ color: C.dim }}>{fmt(n)}</p>
              <p className="text-sm font-semibold mt-2" style={{ color: C.red }}>Beitrag öffnen ›</p>
            </button>
          );
        })}
      </div>
    </MobileShell>
  );
}
